import React, { useState, useEffect } from 'react';
import {
        Paper,
        Typography,
        Box,
        CircularProgress,
        Chip,
        Stack,
        IconButton,
        Tooltip
} from '@mui/material';
import {
        LineChart,
        Line,
        XAxis,
        YAxis,
        CartesianGrid,
        Tooltip as RechartsTooltip,
        ResponsiveContainer,
        Area,
        AreaChart
} from 'recharts';
import {
        Info as InfoIcon,
        TrendingUp,
        TrendingDown,
        SportsScore as RaceIcon
} from '@mui/icons-material';
import axios from 'axios';

interface GhostData {
        status: 'ahead' | 'behind';
        difference: number;
        difference_formatted: string;
        current_total: number;
        ghost_total: number;
        message: string;
        chart_data: Array<{
                day: number;
                current: number | null;
                ghost: number;
                current_formatted?: string;
                ghost_formatted: string;
        }>;
}

export const GhostProfitWidget: React.FC = () => {
        const [data, setData] = useState<GhostData | null>(null);
        const [loading, setLoading] = useState(true);

        useEffect(() => {
                fetchGhostData();
        }, []);

        const fetchGhostData = async () => {
                try {
                        const res = await axios.get('/api/cfo/ghost-mode/test-customer-123');
                        setData(res.data);
                } catch (err) {
                        console.error("Failed to fetch ghost data", err);
                } finally {
                        setLoading(false);
                }
        };

        if (loading) return (
                <Paper sx={{ p: 4, textAlign: 'center', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress />
                </Paper>
        );

        if (!data) return null;

        const isAhead = data.status === 'ahead';

        // Custom Tooltip for Recharts
        const CustomTooltip = ({ active, payload, label }: any) => {
                if (active && payload && payload.length) {
                        return (
                                <Paper sx={{ p: 2, border: '1px solid #e0e0e0', minWidth: 200 }}>
                                        <Typography variant="subtitle2" gutterBottom>Day {label}</Typography>
                                        {payload.map((entry: any, index: number) => (
                                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 2 }}>
                                                        <Typography variant="body2" sx={{ color: entry.color, fontWeight: 500 }}>
                                                                {entry.name}:
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="bold">
                                                                {index === 0 ? entry.payload.current_formatted : entry.payload.ghost_formatted}
                                                        </Typography>
                                                </Box>
                                        ))}
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                {payload[0].value > payload[1].value ? "🚀 Beating the Ghost" : "👻 Ghost is winning"}
                                        </Typography>
                                </Paper>
                        );
                }
                return null;
        };

        return (
                <Paper
                        sx={{
                                p: 3,
                                mb: 4,
                                borderRadius: 4,
                                background: 'linear-gradient(to right, #ffffff, #f8fbfc)',
                                border: '1px solid #e0e7ff',
                                position: 'relative',
                                overflow: 'hidden'
                        }}
                >
                        {/* Background Texture/Ghost Icon? */}
                        <Box sx={{ position: 'absolute', right: -20, top: -20, opacity: 0.05, transform: 'rotate(15deg)' }}>
                                <RaceIcon sx={{ fontSize: 200 }} />
                        </Box>

                        {/* Header Status */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, position: 'relative' }}>
                                <Box>
                                        <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                🏎️ Ghost Mode: Profit Race
                                                <Tooltip title="Compare your current cumulative profit against your previous month (The Ghost). Beat the ghost to stay on track!">
                                                        <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
                                                </Tooltip>
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mt: 1 }}>
                                                <Typography variant="h4" fontWeight="bold" color={isAhead ? 'success.main' : 'error.main'} sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {data.difference_formatted}
                                                        {isAhead ? <TrendingUp sx={{ ml: 1 }} /> : <TrendingDown sx={{ ml: 1 }} />}
                                                </Typography>
                                                <Chip
                                                        label={isAhead ? "AHEAD OF GHOST" : "BEHIND GHOST"}
                                                        color={isAhead ? 'success' : 'error'}
                                                        size="small"
                                                        variant="filled"
                                                        sx={{ fontWeight: 'bold' }}
                                                />
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {data.message}
                                        </Typography>
                                </Box>

                                {/* Mini Legend */}
                                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50' }} />
                                                <Typography variant="caption">You (Now)</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#9e9e9e', border: '2px dashed #666' }} /> // Actually color logic handled in chart
                                                <Typography variant="caption" color="text.secondary">Ghost (Last Month)</Typography>
                                        </Box>
                                </Stack>
                        </Box>

                        {/* Chart Area */}
                        <Box sx={{ width: '100%', height: 300 }}>
                                <ResponsiveContainer>
                                        <AreaChart data={data.chart_data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                                <defs>
                                                        <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#4caf50" stopOpacity={0.2} />
                                                                <stop offset="95%" stopColor="#4caf50" stopOpacity={0} />
                                                        </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                <XAxis
                                                        dataKey="day"
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tick={{ fill: '#9e9e9e', fontSize: 12 }}
                                                        label={{ value: 'Day of Month', position: 'insideBottom', offset: -5, fontSize: 10, fill: '#9e9e9e' }}
                                                />
                                                <YAxis
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tick={{ fill: '#9e9e9e', fontSize: 12 }}
                                                        tickFormatter={(val) => `$${val / 1000}k`}
                                                />
                                                <RechartsTooltip content={<CustomTooltip />} />

                                                {/* Ghost Line (Previous Month) */}
                                                <Area
                                                        type="monotone"
                                                        dataKey="ghost"
                                                        stroke="#9e9e9e"
                                                        strokeDasharray="5 5"
                                                        strokeWidth={2}
                                                        fill="none"
                                                        name="The Ghost"
                                                        animationDuration={1500}
                                                />

                                                {/* Current User Line */}
                                                <Area
                                                        type="monotone"
                                                        dataKey="current"
                                                        stroke="#4caf50"
                                                        strokeWidth={4}
                                                        fill="url(#colorCurrent)"
                                                        name="You"
                                                        animationDuration={2000}
                                                />
                                        </AreaChart>
                                </ResponsiveContainer>
                        </Box>
                </Paper>
        );
};
